'use client'

import { useEffect, useRef, useState } from 'react'

import {
  Bar,
  BarChart as BarCharRecharts,
  CartesianGrid,
  LabelList,
  XAxis,
  YAxis,
} from 'recharts'

import { cn } from '@/lib/cn'

import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '../../chart'

type Payload = {
  label: string
} & Record<string, string | number>

export type StackedBarChartProps = {
  id?: string
  config: ChartConfig
  data: Payload[]
  tooltip?: boolean
  legend?: boolean
  className?: string
  showLabel?: boolean
  yAxisFormatter?: (value: number) => string
  tooltipFormatter?: (value: number) => string
  yAxisDomain?: [number | string, number | string]
}

const StackedBarChart = ({
  id,
  config,
  data,
  tooltip = true,
  legend = true,
  showLabel = true,
  className,
  yAxisFormatter,
  tooltipFormatter,
  yAxisDomain,
}: StackedBarChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [yAxisWidth, setYAxisWidth] = useState(110)

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth
        const calculatedWidth = Math.max(110, containerWidth * 0.1)
        setYAxisWidth(calculatedWidth)
      }
    }

    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  const ignoreKeys = ['label']
  const dynamicKeys = Object.keys(data[0] || {}).filter(
    (key) => !ignoreKeys.includes(key),
  )

  const valueAccessor =
    (attribute: string) =>
    ({ payload }: { payload: Payload }) => {
      return payload[attribute]
    }

  return (
    <ChartContainer
      id={id}
      config={config}
      ref={containerRef}
      className={cn('h-auto min-h-64 w-full', className)}
    >
      <BarCharRecharts
        accessibilityLayer
        data={data}
        margin={{
          top: 20,
        }}
        stackOffset="sign"
      >
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="label"
          tickLine={false}
          tickMargin={8}
          axisLine={false}
        />
        <YAxis
          type="number"
          tickLine={false}
          tickMargin={12}
          axisLine={false}
          tickFormatter={yAxisFormatter}
          width={yAxisWidth}
          domain={yAxisDomain}
        />

        {tooltip && (
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                valueFormatter={(value) => {
                  const number = Number(value.replace(',', ''))
                  return tooltipFormatter ? tooltipFormatter(number) : value
                }}
                hideLabel
              />
            }
          />
        )}

        {legend && <ChartLegend content={<ChartLegendContent />} />}

        {dynamicKeys.map((dataKey) => {
          const colorConfig = config[dataKey]
          if (colorConfig?.chartClassName) {
            return (
              <Bar
                key={`bar-${dataKey}`}
                stackId="first"
                dataKey={dataKey}
                className={colorConfig.chartClassName}
              >
                {showLabel && (
                  <LabelList
                    position="center"
                    valueAccessor={valueAccessor(dataKey)}
                    className="fill-system-neutral-white text-xs font-medium"
                  />
                )}
              </Bar>
            )
          }

          return (
            <Bar
              key={`bar-${dataKey}`}
              stackId="first"
              dataKey={dataKey}
              fill={`var(--color-${dataKey})`}
            >
              {showLabel && (
                <LabelList
                  position="center"
                  valueAccessor={valueAccessor(dataKey)}
                  className="fill-system-neutral-white text-xs font-medium"
                />
              )}
            </Bar>
          )
        })}
      </BarCharRecharts>
    </ChartContainer>
  )
}
StackedBarChart.displayName = 'StackedBarChart'

export { StackedBarChart }
